<template>
	<section class="view-signup-enterpassword" v-bind:class="stateClass">
		<header class="stepped-overlay-header">
			<ChalkyBrandingBadge size="md" />
			<h3>Enter a password</h3>
			<p>Minimum of 8 characters</p>
		</header>

		<section class="fields stepped-overlay-content">
			<fieldset>
				<label>
					<h6>Password</h6>
					<input
						autocomplete="current-password"
						minlength="8"
						name="password"
						placeholder="Password"
						ref="passwordInput"
						required
						type="password"
						v-model="authModel.attributes.password"
						v-on:keyup.enter="Handle_OnClickRegister"
					/>
					<p class="type-error" v-if="passwordError">{{ passwordError }}</p>
				</label>

				<label>
					<h6>Confirm Password</h6>
					<input
						autocomplete="current-password"
						minlength="8"
						name="confirm-password"
						placeholder="Confirm Password"
						required
						type="password"
						v-model="authModel.attributes.password_confirmation"
						v-on:keyup.enter="Handle_OnClickRegister"
					/>
					<p class="type-error" v-if="passwordConfirmationError">{{ passwordConfirmationError }}</p>
				</label>
			</fieldset>

			<div class="alert size-compact push-bottom" v-if="this.signupFailed">
				<div class="content">
					<h6>Could not sign up</h6>
					<p class="alert-text type-small-paragraph">{{ message }}</p>
				</div>
			</div>
		</section>

		<section class="actions stepped-overlay-actions">
			<button class="button-secondary" v-on:click="Handle_OnClickBack">Back</button>
			<button class="button-primary" v-on:click="Handle_OnClickRegister">Register</button>
		</section>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Signup
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewSignupEnterPassword extends ViewBase {
		/**
		 * CSS state class based on signup status.
		 *
		 * @return string
		 */
		public get stateClass(): string {
			if (this.authModel?.isLoggedIn()) {
				return 'state-signup-success';
			}

			if (this.signupFailed) {
				return 'state-signup-failed';
			}

			if (this.authModel?.loading) {
				return 'state-loading';
			}

			return 'state-default';
		}

		/**
		 * @type HTMLInputElement
		 */
		@Ref('passwordInput')
		readonly passwordInput!: HTMLInputElement;

		/**
		 * @type ChalkySticks/Model/Authentication
		 */
		@Prop()
		public authModel!: ChalkySticks.Model.Authentication;

		/**
		 * @type ChalkySticks.Model.User
		 */
		@Prop()
		protected userModel!: ChalkySticks.Model.User;

		/**
		 * @type string
		 */
		protected message: string = '';

		/**
		 * Error message for the password field
		 *
		 * @type string
		 */
		protected passwordError: string = '';

		/**
		 * Error message for the password confirmation field
		 *
		 * @type string
		 */
		protected passwordConfirmationError: string = '';

		/**
		 * @type boolean
		 */
		protected signupFailed: boolean = false;

		/**
		 * Focus the password input field when component is mounted
		 *
		 * @return void
		 */
		@mounted
		protected focusFirstInput(): void {
			this.$nextTick(() => this.passwordInput.focus());
		}

		/**
		 * Validates all form inputs and sets appropriate error messages
		 *
		 * @return boolean
		 */
		protected validateFormInputs(): boolean {
			let isValid = true;

			// Clear any previous validation errors
			this.clearValidationErrors();

			// Validate password field
			if (!this.validatePasswordField(this.authModel.attributes.password)) {
				isValid = false;
			}

			// Validate password confirmation field
			if (!this.validatePasswordConfirmationField(this.authModel.attributes.password_confirmation)) {
				isValid = false;
			}

			return isValid;
		}

		/**
		 * Validates the password field and sets error message if invalid
		 *
		 * @param string password
		 * @return boolean
		 */
		protected validatePasswordField(password: string): boolean {
			if (!password) {
				this.passwordError = 'Please enter a password.';
				return false;
			}

			if (password.length < 8) {
				this.passwordError = 'Password must be at least 8 characters long.';
				return false;
			}

			return true;
		}

		/**
		 * Validates the password confirmation field and sets error message if invalid
		 *
		 * @param string passwordConfirmation
		 * @return boolean
		 */
		protected validatePasswordConfirmationField(passwordConfirmation: string): boolean {
			if (!passwordConfirmation) {
				this.passwordConfirmationError = 'Please confirm your password.';
				return false;
			}

			if (passwordConfirmation !== this.authModel.attributes.password) {
				this.passwordConfirmationError = 'Passwords do not match.';
				return false;
			}

			return true;
		}

		/**
		 * Clears all validation error messages
		 *
		 * @return void
		 */
		protected clearValidationErrors(): void {
			this.passwordError = '';
			this.passwordConfirmationError = '';
		}

		/**
		 * Navigates to the previous step in the signup process
		 *
		 * @return void
		 */
		protected previous(): void {
			this.$router.push({ params: { step: 'create-account' } });
		}

		/**
		 * Navigates to the next step in the signup process
		 *
		 * @return void
		 */
		protected next(): void {
			// this.$router.push({ params: { step: 'profile-photo' } });
			this.$router.push({ params: { step: 'favorite-games' } });
		}

		/**
		 * Signup via email
		 *
		 * @return Promise<ChalkySticks.Model.User>
		 */
		public async signup(): Promise<ChalkySticks.Model.User> {
			// Reset signup failure
			this.signupFailed = false;

			// Attempt signup
			try {
				// Sign up to ChalkySticks
				const userModel = await this.authModel.signup(this.authModel.attributes as any);

				// Authorize model and set data
				this.userModel.auth().set(userModel.attributes);

				// Move onward
				this.next();
			} catch (e: any) {
				this.signupFailed = true;
				this.message = e.message || 'An error occurred during signup. Please try again.';

				setTimeout(() => (this.signupFailed = false), 1000 * 3);
			}

			return this.userModel;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		public async Handle_OnClickBack(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.previous();
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		public async Handle_OnClickRegister(e: PointerEvent): Promise<void> {
			e.preventDefault();

			// Validate the current form state
			const isFormValid = this.validateFormInputs();

			// If validation passes, proceed to next step
			isFormValid && this.signup();
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-signup-enterpassword {
		.type-error {
			opacity: 1 !important;
		}
	}
</style>
